import {
  Shape,
  WallShape,
  WallPathShape
} from "../types";

export const FIT_PADDING = 50;

/**
 * Compute tight bounding box around all shapes, accounting for walls and wall-paths.
 * Returns null when there are no shapes.
 */
export function computeShapesBounds(
  shapes: Shape[],
  padding = FIT_PADDING
): { x: number; y: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const s of shapes) {
    if (s.type === "wall") {
      const w = s as WallShape;
      const half = (w.thickness || 6) / 2;
      minX = Math.min(minX, w.x - half, w.x2 - half);
      minY = Math.min(minY, w.y - half, w.y2 - half);
      maxX = Math.max(maxX, w.x + half, w.x2 + half);
      maxY = Math.max(maxY, w.y + half, w.y2 + half);
    } else if (s.type === "wall-path") {
      const wp = s as WallPathShape;
      const half = (wp.thickness || 6) / 2;
      for (const v of wp.vertices) {
        minX = Math.min(minX, v.x - half);
        minY = Math.min(minY, v.y - half);
        maxX = Math.max(maxX, v.x + half);
        maxY = Math.max(maxY, v.y + half);
      }
    } else {
      minX = Math.min(minX, s.x);
      minY = Math.min(minY, s.y);
      maxX = Math.max(maxX, s.x + s.width);
      maxY = Math.max(maxY, s.y + s.height);
    }
  }

  if (!isFinite(minX)) return null;

  return {
    x: minX - padding,
    y: minY - padding,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2
  };
}

/**
 * Shift all shapes so the content origin aligns with (offsetX, offsetY).
 * Returns a new array — does not mutate the input.
 */
export function shiftShapes(shapes: Shape[], dx: number, dy: number): Shape[] {
  if (dx === 0 && dy === 0) return shapes;

  return shapes.map((s) => {
    const base = { ...s, x: s.x + dx, y: s.y + dy };
    if (s.type === "wall") {
      const w = s as WallShape;
      return { ...base, x2: w.x2 + dx, y2: w.y2 + dy } as Shape;
    }
    if (s.type === "wall-path") {
      const wp = s as WallPathShape;
      return {
        ...base,
        vertices: wp.vertices.map((v) => ({ ...v, x: v.x + dx, y: v.y + dy }))
      } as Shape;
    }
    return base as Shape;
  });
}
